import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePreferredGenresContext } from '@/contexts/PreferredGenresContext'
import { useGenres } from '@/hooks/genres'

interface GenreMultiSelectProps {
  selectedGenres: string[]
  onGenresChange: (genreIds: string[]) => void
  error?: string
  className?: string
  preferredFirst?: boolean
}

export function GenreMultiSelect({
  selectedGenres,
  onGenresChange,
  error,
  className = '',
  preferredFirst = true,
}: GenreMultiSelectProps) {
  const { t } = useTranslation()
  const { getPreferredGenreIds, hasPreferredGenres } = usePreferredGenresContext()

  const { data: genres, isLoading } = useGenres({
    preferredFirst: preferredFirst && hasPreferredGenres,
    preferredGenreIds: getPreferredGenreIds(),
  })
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const handleGenreToggle = (genreId: string) => {
    if (selectedGenres.includes(genreId)) {
      onGenresChange(selectedGenres.filter((id) => id !== genreId))
    } else {
      onGenresChange([...selectedGenres, genreId])
    }
  }

  const filteredGenres = useMemo(() => {
    if (!genres) return []
    if (!searchTerm.trim()) return genres

    return genres.filter((genre) => genre.name.toLowerCase().includes(searchTerm.toLowerCase()))
  }, [genres, searchTerm])

  const selectedGenreNames =
    genres?.filter((genre) => selectedGenres.includes(genre.id)).map((genre) => genre.name) || []

  const renderGenresByPreference = () => {
    if (!filteredGenres) return null

    const preferredGenreIds = getPreferredGenreIds()
    const preferredGenres = filteredGenres.filter((genre) => preferredGenreIds.includes(genre.id))
    const otherGenres = filteredGenres.filter((genre) => !preferredGenreIds.includes(genre.id))

    const renderGenreItem = (genre: (typeof filteredGenres)[0]) => (
      <label
        key={genre.id}
        className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 hover:bg-muted"
      >
        <input
          type="checkbox"
          checked={selectedGenres.includes(genre.id)}
          onChange={() => handleGenreToggle(genre.id)}
          className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
        />
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full" style={{ backgroundColor: genre.color }} />
          <span className="text-sm font-medium text-foreground">{genre.name}</span>
        </div>
      </label>
    )

    const sections = []

    // Preferred genres section
    if (preferredGenres.length > 0) {
      sections.push(
        <div key="preferred">
          <div className="text-xs font-medium text-primary px-3 py-1 border-b border-primary/20 mb-2">
            {t('settings.preferredGenres.suggestions')}
          </div>
          <div className="space-y-1">{preferredGenres.map(renderGenreItem)}</div>
        </div>
      )
    }

    // Other genres section
    if (otherGenres.length > 0) {
      sections.push(
        <div key="others">
          {preferredGenres.length > 0 && (
            <div className="text-xs font-medium text-muted-foreground px-3 py-1 border-b border-border/20 mb-2">
              {t('settings.preferredGenres.otherGenres')}
            </div>
          )}
          <div className="space-y-1">{otherGenres.map(renderGenreItem)}</div>
        </div>
      )
    }

    return sections
  }

  if (isLoading) {
    return (
      <div
        className={`w-full rounded-lg border border-border bg-card px-3 py-2 text-sm ${className}`}
      >
        <span className="text-muted-foreground">{t('common.loading')}...</span>
      </div>
    )
  }

  return (
    <div className={`relative ${className}`}>
      {/* Selected genres display / dropdown trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full rounded-lg border bg-card px-3 py-2 text-left text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
          error ? 'border-error' : 'border-border'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {selectedGenreNames.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {selectedGenreNames.map((name) => (
                  <span
                    key={name}
                    className="inline-flex items-center rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
                  >
                    {name}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-muted-foreground">{t('books.fields.selectGenres')}</span>
            )}
          </div>
          <svg
            className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <>
          {/* Backdrop to close dropdown */}
          <button
            type="button"
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
            aria-label="Close dropdown"
          />

          {/* Dropdown content */}
          <div className="absolute z-20 mt-1 w-full rounded-lg border border-border bg-card shadow-lg">
            {/* Search input */}
            <div className="border-b border-border p-3">
              <input
                type="text"
                placeholder={t('books.fields.searchGenres')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-md border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <div className="max-h-60 overflow-auto p-2">
              {filteredGenres && filteredGenres.length > 0 ? (
                <div className="space-y-3">{renderGenresByPreference()}</div>
              ) : (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  {searchTerm.trim()
                    ? t('books.fields.noGenresFound')
                    : t('books.fields.noGenresAvailable')}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
